import { parseComposerReferences } from '@runloop/reflex-contract';
import { truncate } from '@reflex/ui/lib/format';
import type { LightboxImage } from '@reflex/ui/components/content/ImagePreview';
import type { ChatItem, ChatMessage, TurnBlock } from './types.js';

/**
 * Turn navigation for the transcript. A turn starts at a user message and runs
 * until the next one, so the nav lists prompts: the reader scans what they
 * asked, not what the agent replied. Anchors are DOM ids on the transcript's
 * user-message rows, which is what `SectionNav` scrolls to and observes.
 *
 * Each entry also carries how the turn ended and any images it produced, so the
 * rail says something about the work and not only about the prompt.
 */

/** Id of the zero-height sentinel at the very start of the mounted transcript. */
export const CHAT_START_ANCHOR_ID = 'chat-turn-nav-start';

/** Id of the zero-height sentinel at the very end of the transcript. */
export const CHAT_END_ANCHOR_ID = 'chat-turn-nav-end';

/**
 * Most turns the rail lists between its pinned ends.
 *
 * The rail is an index, not a table of contents: a dozen readable rows that
 * follow the turn you are reading beat a wall of near-identical truncated ones.
 * It is also what keeps the rail's cost fixed — the transcript mounts a bounded
 * window by default, but "Show earlier messages" is unbounded, and a fully
 * revealed thousand-turn agent would otherwise mean a thousand-entry nav with an
 * IntersectionObserver watching every one. Older turns stay reachable by
 * scrolling, and the rail says so with a jump to the top of what is mounted.
 */
export const MAX_RAIL_TURNS = 12;

/** Longest turn label before it is ellipsised. The rail truncates visually too; hover has the rest. */
const MAX_LABEL_CHARS = 40;

/** Longest prompt shown on hover. Enough to recognise a turn, short of a wall of text. */
export const MAX_HOVER_CHARS = 240;

/** Leading markdown that carries no meaning in a one-line label. */
const LEADING_MARKUP = /^[>\-*#`\s]+/;

/**
 * How a turn ended, as far as the rail cares.
 *
 * Deliberately coarser than the turn footer's copy: at rail size the question a
 * reader is asking is "did that one work, and is anything waiting on me", not
 * which of three error subtypes it was.
 */
export type TurnStatus =
  | 'done'
  | 'error'
  | 'interrupted'
  | 'question'
  | 'working'
  /** No answer yet and nothing in flight — a prompt sent while the agent was busy. */
  | 'unanswered';

export interface TurnEntry {
  /** DOM id of the transcript row this entry scrolls to. */
  id: string;
  /** Position in the mounted thread, counting from the oldest mounted turn. */
  number: number;
  /**
   * Axon sequence of the turn's opening prompt event, when the message knows
   * it. What the unified rail matches loaded turns to session-index rows by.
   */
  seqNumber?: number;
  label: string;
  /**
   * The prompt as written, for hover. Set only when it says more than the row
   * already shows — a tooltip that repeats its own label is noise.
   */
  title?: string;
  /**
   * Original stored prompt (may include expanded `<referenced-*>` blocks). The
   * rail tooltip re-parses this so references render as badges instead of plain
   * titles.
   */
  content: string;
  status: TurnStatus;
  /** Images the agent produced during the turn, in the order they arrived. */
  images: LightboxImage[];
}

export interface TurnNav {
  turns: TurnEntry[];
  /** Every turn in the mounted transcript, including any the rail did not list. */
  totalTurns: number;
  /** True when older mounted turns were dropped to keep the rail a fixed size. */
  truncated: boolean;
}

/**
 * Anchor id for a turn. Message ids come from the event stream, so they are
 * namespaced rather than used raw — an id colliding with something else on the
 * page would send `getElementById` to the wrong element.
 */
export function turnAnchorId(messageId: string): string {
  return `chat-turn-${messageId}`;
}

/**
 * Collapse composer references (compact markers or expanded blocks) to their
 * titles so the rail never shows raw `<referenced-*>` markup. Matches what
 * {@link MarkdownWithAgentRefs} renders as badges in the transcript.
 */
function displayPromptText(content: string): string {
  return parseComposerReferences(content)
    .map((seg) => (seg.type === 'text' ? seg.value : seg.title))
    .join('');
}

/** The prompt's first non-empty line, stripped of markup and collapsed to one line. */
function promptSummary(content: string): string {
  const firstLine = displayPromptText(content)
    .split('\n')
    .map((line) => line.replace(LEADING_MARKUP, '').trim())
    .find((line) => line.length > 0);
  return firstLine?.replace(/\s+/g, ' ').trim() ?? '';
}

/**
 * One-line label for a prompt, shortened to fit the rail. A prompt with no text
 * of its own (attachments only) says so rather than rendering a blank row; the
 * turn number beside it is what identifies it.
 */
export function turnLabel(content: string): string {
  const summary = promptSummary(content);
  return summary ? truncate(summary, MAX_LABEL_CHARS) : 'No prompt text';
}

/**
 * The prompt as written, trimmed and capped: what a reader hovers a truncated
 * row to see. Blank runs between paragraphs collapse so a long prompt reads as
 * a block rather than a scroll. References collapse to titles first so hover
 * never dumps expanded context XML.
 */
function hoverText(content: string): string {
  const trimmed = displayPromptText(content)
    .trim()
    .replace(/\n{2,}/g, '\n');
  return trimmed.length > MAX_HOVER_CHARS
    ? `${trimmed.slice(0, MAX_HOVER_CHARS).trimEnd()}\u2026`
    : trimmed;
}

function isAssistant(item: ChatItem): item is ChatMessage {
  return item.role === 'assistant';
}

/** Blocks the agent produced in a turn, across however many assistant messages it took. */
function turnBlocks(items: ChatItem[]): TurnBlock[] {
  return items.filter(isAssistant).flatMap((message) => message.blocks ?? []);
}

/** Images the agent produced, ready for the lightbox. */
export function turnImages(blocks: TurnBlock[]): LightboxImage[] {
  return blocks
    .filter((block): block is Extract<TurnBlock, { type: 'image' }> => block.type === 'image')
    .map((block, index) => ({
      src: block.uri ?? `data:${block.mimeType};base64,${block.data}`,
      alt: `Agent output image ${index + 1}`,
    }));
}

/**
 * A prompt the agent is still waiting on. Same rule the transcript window uses
 * to decide a row must stay mounted: while one of these is open the turn cannot
 * proceed, which is the most useful thing the rail can say about it.
 */
function hasOpenQuestion(blocks: TurnBlock[]): boolean {
  return blocks.some(
    (block) =>
      (block.type === 'control_request' || block.type === 'codex_approval') && !block.responded,
  );
}

/**
 * A skipped or dismissed AskUserQuestion ends the turn with a denied tool use,
 * which the result reports as an error. That is a deliberate user action rather
 * than a failure, and `AssistantTurn` does not paint it red either.
 */
function endedByDeclinedQuestion(blocks: TurnBlock[]): boolean {
  return blocks.some(
    (block) =>
      block.type === 'tool_call' && block.title === 'AskUserQuestion' && block.status === 'failed',
  );
}

const INTERRUPTED_STOP_REASONS = new Set(['cancelled', 'interrupted']);

function turnStatus(items: ChatItem[], blocks: TurnBlock[], isLive: boolean): TurnStatus {
  if (hasOpenQuestion(blocks)) return 'question';

  const answers = items.filter(isAssistant);
  if (answers.length === 0) return isLive ? 'working' : 'unanswered';

  const declined = endedByDeclinedQuestion(blocks);
  if (answers.some((answer) => answer.isError) && !declined) return 'error';
  if (
    answers.some(
      (answer) =>
        answer.stopReason && INTERRUPTED_STOP_REASONS.has(answer.stopReason.toLowerCase()),
    )
  ) {
    return 'interrupted';
  }
  return isLive ? 'working' : 'done';
}

/**
 * Build the rail from the transcript rows that are currently mounted: the newest
 * `MAX_RAIL_TURNS` turns, numbered by their real position, each with how it went
 * and what it produced.
 *
 * Only mounted rows qualify: the transcript windows older history out of the
 * DOM (see `transcript-window.ts`), and a nav entry whose anchor does not exist
 * is a link that silently does nothing. Readers reach older turns through "Show
 * earlier messages", which widens the window and the rail with it.
 *
 * Lists a lone turn like any other. Whether one turn is worth a rail depends on
 * things this scan cannot see — how tall that turn grew, whether history sits
 * above it — so the caller decides; see `ChatTurnNav`.
 */
export function buildTurnNav(
  mountedMessages: ChatItem[],
  live?: {
    /** Blocks of the turn currently streaming, which belong to the newest prompt. */
    blocks?: readonly TurnBlock[];
    /** True while the agent is working, so the newest turn reads as in progress. */
    isGenerating?: boolean;
  },
  /**
   * Most turns to list. The default keeps the legacy rail a fixed size; the
   * unified index-backed rail passes `Infinity` and scrolls instead (see
   * `buildUnifiedTurnRail`).
   */
  maxTurns = MAX_RAIL_TURNS,
): TurnNav {
  // Split the transcript at each prompt: a turn is the prompt plus everything
  // up to the next one.
  const groups: Array<{ message: ChatMessage; items: ChatItem[] }> = [];
  for (const item of mountedMessages) {
    if (item.role === 'user') {
      groups.push({ message: item as ChatMessage, items: [] });
    } else if (groups.length > 0) {
      groups[groups.length - 1].items.push(item);
    }
  }

  const totalTurns = groups.length;
  const entries: TurnEntry[] = groups.map((group, index) => {
    const isNewest = index === totalTurns - 1;
    const blocks = turnBlocks(group.items);
    // The in-flight turn's blocks have not been folded into a message yet, so
    // the newest prompt has to be told about them or it would look unanswered
    // while the agent is visibly working under it.
    const liveBlocks = isNewest && live?.blocks?.length ? [...blocks, ...live.blocks] : blocks;
    const content = group.message.content;
    const label = turnLabel(content);
    const hover = hoverText(content);
    const seqNumber = group.message.sourceEvent?.sequence;
    return {
      id: turnAnchorId(group.message.id),
      number: index + 1,
      ...(seqNumber !== undefined ? { seqNumber } : {}),
      label,
      content,
      ...(hover && hover !== label ? { title: hover } : {}),
      status: turnStatus(group.items, liveBlocks, Boolean(isNewest && live?.isGenerating)),
      images: turnImages(liveBlocks),
    };
  });

  return {
    turns: Number.isFinite(maxTurns) ? entries.slice(-maxTurns) : entries,
    totalTurns,
    truncated: entries.length > maxTurns,
  };
}
